import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { loadCertificatePreviewFonts, registerCertificatePdfFonts } from "../../../utils/certificatePdfFonts";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Add,
  AddPhotoAlternateOutlined,
  DeleteOutline,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  QrCode2Outlined,
} from "@mui/icons-material";
import {
  createCertificateTemplate,
  findCertificateTemplate,
  findCertificateTemplateVariables,
  updateCertificateTemplate,
} from "../../../API/apiCertificateTemplates";
import styles from "./CadastrarModeloCertificado.module.css";
import stylesGrid from "../../../styles/Grid.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesButton from "../../../styles/Button.module.css";
import stylesGeneral from "../../../styles/General.module.css";
import {
  BackgroundImage,
  CertificatePage,
  CertificateTemplateDetail,
  CertificateTemplatePayload,
  CertificateTemplateVariable,
  CertificadoElemento,
  CertificadoImagem,
  CertificadoQrCode,
  CertificadoTexto,
  DocumentType,
  SideKey,
} from "../../../types/CertificateTemplate";

type PdfTextToken = {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  lineBreak?: boolean;
};

type SidebarTab = "elements" | "variables";

const isTextoElemento = (element: CertificadoElemento): element is CertificadoTexto => {
  return element.type === "html_text";
};

const isImagemElemento = (element: CertificadoElemento): element is CertificadoImagem => {
  return element.type === "image";
};

const isQrCodeElemento = (element: CertificadoElemento): element is CertificadoQrCode => {
  return element.type === "validation_qrcode";
};

const fontesDisponiveis = [
  "Arial",
  "Times New Roman",
  "Montserrat",
  "Poppins",
  "Georgia",
  "Verdana",
];

const criarTextosPadrao = (removable = false): CertificadoTexto[] => [
  {
    id: "titulo",
    label: "Titulo",
    role: "title",
    type: "html_text",
    removable,
    contentHtml: "<p><strong>Certificado</strong></p>",
    style: {
      fontFamily: "Montserrat",
      fontSize: 34,
      color: "#1F2937",
      align: "center",
    },
    position: { x: 110, y: 70 },
    size: { width: 680 },
  },
  {
    id: "subtitulo",
    label: "Subtitulo",
    role: "subtitle",
    type: "html_text",
    removable,
    contentHtml: "<p>Conclusao de curso</p>",
    style: {
      fontFamily: "Poppins",
      fontSize: 18,
      color: "#374151",
      align: "center",
    },
    position: { x: 170, y: 130 },
    size: { width: 560 },
  },
  {
    id: "nome",
    label: "Nome",
    role: "recipient_name",
    type: "html_text",
    removable,
    contentHtml: "<p><strong>{{nome}}</strong></p>",
    style: {
      fontFamily: "Montserrat",
      fontSize: 28,
      color: "#111827",
      align: "center",
    },
    position: { x: 140, y: 210 },
    size: { width: 620 },
  },
  {
    id: "texto-principal",
    label: "Texto Principal",
    role: "main_text",
    type: "html_text",
    removable,
    contentHtml:
      "<p>Certificamos que <strong>{{nome}}</strong> concluiu o curso <strong>{{curso}}</strong>, com Carga Horária de <strong>{{cargaHoraria}}</strong> horas.</p>",
    style: {
      fontFamily: "Times New Roman",
      fontSize: 20,
      color: "#111827",
      align: "center",
    },
    position: { x: 120, y: 300 },
    size: { width: 660 },
  },
];

const dadosExemplo: Record<string, string> = {
  "{{nome}}": "Maria Aparecida da Silva",
  "{{cpf}}": "123.456.789-09",
  "{{email}}": "maria.silva@exemplo.com.br",
  "{{curso}}": "Gestão do SUAS e Proteção Social",
  "{{turma}}": "Turma 01 - Manhã",
  "{{cargaHoraria}}": "40",
  "{{dataConclusao}}": "28/05/2026",
  "{{dataInicio}}": "12/05/2026",
  "{{dataFim}}": "28/05/2026",
  "{{instituicao}}": "SEDESE/MG",
  "{{municipio}}": "Belo Horizonte",
  "{{codigoValidacao}}": "HEI-1660002",
  "{{dataEmissao}}": "28/05/2026 15:31:47",
  "{{turma.modalidade}}": "Presencial",
  "{{turma.hora_inicio}}": "08:00",
  "{{turma.hora_fim}}": "12:00",
  "{{turma.endereco}}": "Rua da Bahia, 1148 - Centro",
  "{{turma.cep}}": "30160-011",
  "{{turma.logradouro}}": "Rua da Bahia",
  "{{turma.numero}}": "1148",
  "{{turma.complemento}}": "Auditório principal",
  "{{turma.bairro}}": "Centro",
  "{{turma.cidade}}": "Belo Horizonte",
  "{{turma.uf}}": "MG",
  "{{instituicao_resp}}": "Diretoria de Educação Permanente",
  "{{acao.modalidade}}": "Presencial",
  "{{acao.tipologia}}": "Capacitação - Curso",
  "{{acao.temas}}": "Gestão do SUAS, Proteção Social e Vigilância Socioassistencial",
  "{{codigo_certificado}}": "HEI-1660002",
  "{{data_emissao_certificado}}": "28/05/2026 15:31:47",
};

const qrCodeValidationRoute = "/validar-certificado?chave={{codigo_certificado}}";

const qrCodeValidacaoPadrao = {
  validationUrl: qrCodeValidationRoute,
  validationKey: "{{codigo_certificado}}",
  issueDate: "{{data_emissao_certificado}}",
  institutionText: "Certificado registrado pela instituição Faculdade Pólis Civitas",
  instructionText:
    "O presente certificado pode ter a validade comprovada acessando o QRCODE a esquerda, ou, caso desejar, informando a chave acima na opção validar certificado no endereço https://participei.com",
};

const qrCodeValidacaoConfig = {
  ...qrCodeValidacaoPadrao,
  institutionText: "",
  instructionText:
    "Certificado registrado pela instituicao {{instituicao_resp}}\nChave de validacao: {{codigo_certificado}}. Data de emissao: {{data_emissao_certificado}}\nO presente certificado pode ter a validade comprovada acessando o QR Code ao lado ou pela rota /validar-certificado informando a chave acima.",
};

const preencherVariaveisExemplo = (text: string) =>
  Object.entries(dadosExemplo)
    .reduce(
    (resultado, [variavel, valor]) => resultado.replaceAll(variavel, valor),
    text,
  )
    .replace(/\{\{[^}]+\}\}/g, "Dado de exemplo");

const renderizarHtmlComVariaveis = (html: string) => preencherVariaveisExemplo(html);

const renderizarTextoComVariaveis = (text?: string | null) => {
  return preencherVariaveisExemplo(text ?? "");
};

const buildQrValidationUrl = (validationUrl: string) => {
  const renderedUrl = renderizarTextoComVariaveis(validationUrl);

  try {
    return new URL(renderedUrl, window.location.origin).toString();
  } catch {
    return renderedUrl;
  }
};

const parseOptionalNumber = (value: string) => {
  return value === "" ? undefined : Number(value);
};

const inputNumberValue = (value?: number) => {
  return value ?? "";
};

const numberOrDefault = (value: number | undefined, fallback: number) => {
  return typeof value === "number" && !Number.isNaN(value) ? value : fallback;
};

const getPdfPageSize = (orientation: "landscape" | "portrait") => {
  return orientation === "landscape"
    ? { width: 900, height: 636 }
    : { width: 636, height: 900 };
};

const getPdfFontFamily = (fontFamily: string) => {
  if (fontFamily.toLowerCase().includes("montserrat")) return "Montserrat";
  if (fontFamily.toLowerCase().includes("poppins")) return "Poppins";
  if (fontFamily.toLowerCase().includes("times")) return "times";
  if (fontFamily.toLowerCase().includes("courier")) return "courier";
  return "helvetica";
};

const getPreviewFontFamily = (fontFamily: string) => {
  if (fontFamily.toLowerCase().includes("montserrat")) return '"Certificate Montserrat", Montserrat, sans-serif';
  if (fontFamily.toLowerCase().includes("poppins")) return '"Certificate Poppins", Poppins, sans-serif';
  return fontFamily;
};

const getPdfFontStyle = (token: PdfTextToken) => {
  if (token.bold && token.italic) return "bolditalic";
  if (token.bold) return "bold";
  if (token.italic) return "italic";
  return "normal";
};

const getImageFormat = (dataUrl: string) => {
  if (dataUrl.includes("image/jpeg") || dataUrl.includes("image/jpg")) return "JPEG";
  if (dataUrl.includes("image/webp")) return "WEBP";
  return "PNG";
};

const htmlToPdfTokens = (html: string): PdfTextToken[] => {
  const documentHtml = new DOMParser().parseFromString(html || "", "text/html");
  const tokens: PdfTextToken[] = [];

  const walk = (
    node: ChildNode,
    marks: { bold: boolean; italic: boolean; underline: boolean }
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent) {
        tokens.push({
          text: renderizarHtmlComVariaveis(node.textContent),
          ...marks,
        });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const nextMarks = {
      bold: marks.bold || ["strong", "b"].includes(tag),
      italic: marks.italic || ["em", "i"].includes(tag),
      underline: marks.underline || tag === "u",
    };

    if (tag === "br") {
      tokens.push({ text: "", ...marks, lineBreak: true });
      return;
    }

    Array.from(element.childNodes).forEach((child) => walk(child, nextMarks));

    if (["p", "div"].includes(tag)) {
      tokens.push({ text: "", ...marks, lineBreak: true });
    }
  };

  Array.from(documentHtml.body.childNodes).forEach((child) =>
    walk(child, { bold: false, italic: false, underline: false })
  );

  while (tokens[tokens.length - 1]?.lineBreak) {
    tokens.pop();
  }

  return tokens.length ? tokens : [{ text: "", bold: false, italic: false, underline: false }];
};

const renderHtmlTextInPdf = (doc: jsPDF, element: CertificadoTexto) => {
  const tokens = htmlToPdfTokens(element.contentHtml);
  const fontFamily = getPdfFontFamily(element.style.fontFamily);
  const fontSize = element.style.fontSize;
  const lineHeight = fontSize * 1.25;
  const maxWidth = numberOrDefault(element.size.width, 200);
  const positionX = numberOrDefault(element.position.x, 0);
  const positionY = numberOrDefault(element.position.y, 0);

  type LinePart = PdfTextToken & { width: number };
  const lines: LinePart[][] = [[]];
  const lineWidths: number[] = [0];

  const pushLine = () => {
    lines.push([]);
    lineWidths.push(0);
  };

  tokens.forEach((token) => {
    if (token.lineBreak) {
      pushLine();
      return;
    }

    const parts = token.text.split(/(\s+)/);
    parts.forEach((part) => {
      if (!part) return;

      doc.setFont(fontFamily, getPdfFontStyle(token));
      doc.setFontSize(fontSize);
      const partWidth = doc.getTextWidth(part);
      const currentLineIndex = lines.length - 1;
      const currentWidth = lineWidths[currentLineIndex];
      const isOnlySpace = part.trim().length === 0;

      if (!isOnlySpace && currentWidth > 0 && currentWidth + partWidth > maxWidth) {
        pushLine();
      }

      const nextLineIndex = lines.length - 1;
      lines[nextLineIndex].push({ ...token, text: part, width: partWidth });
      lineWidths[nextLineIndex] += partWidth;
    });
  });

  lines.forEach((line, lineIndex) => {
    const lineWidth = lineWidths[lineIndex];
    const offsetX =
      element.style.align === "center"
        ? (maxWidth - lineWidth) / 2
        : element.style.align === "right"
          ? maxWidth - lineWidth
          : 0;
    let cursorX = positionX + Math.max(offsetX, 0);
    const cursorY = positionY + fontSize + lineIndex * lineHeight;

    line.forEach((part) => {
      doc.setFont(fontFamily, getPdfFontStyle(part));
      doc.setFontSize(fontSize);
      doc.setTextColor(element.style.color);
      doc.text(part.text, cursorX, cursorY);

      if (part.underline && part.text.trim()) {
        doc.setDrawColor(element.style.color);
        doc.line(cursorX, cursorY + 2, cursorX + part.width, cursorY + 2);
      }

      cursorX += part.width;
    });
  });
};

const renderImageInPdf = (doc: jsPDF, element: CertificadoImagem) => {
  try {
    doc.addImage(
      element.dataUrl,
      getImageFormat(element.dataUrl),
      numberOrDefault(element.position.x, 0),
      numberOrDefault(element.position.y, 0),
      numberOrDefault(element.size.width, 100),
      numberOrDefault(element.size.height, 100)
    );
  } catch (error) {
    console.error("Erro ao adicionar imagem no PDF:", error);
  }
};

const _renderQrCodeInPdf = (doc: jsPDF, element: CertificadoQrCode) => {
  try {
    doc.addImage(
      element.qrCodeDataUrl,
      getImageFormat(element.qrCodeDataUrl),
      numberOrDefault(element.position.x, 0),
      numberOrDefault(element.position.y, 0),
      numberOrDefault(element.qrSize, 100),
      numberOrDefault(element.qrSize, 100)
    );

    const fontFamily = getPdfFontFamily(element.style.fontFamily);
    const textX =
      element.textPosition === "right"
        ? numberOrDefault(element.position.x, 0) + numberOrDefault(element.qrSize, 100) + 10
        : numberOrDefault(element.position.x, 0);
    const textY =
      element.textPosition === "right"
        ? numberOrDefault(element.position.y, 0) + element.style.fontSize
        : numberOrDefault(element.position.y, 0) + numberOrDefault(element.qrSize, 100) + element.style.fontSize + 8;
    const textWidth =
      element.textPosition === "right"
        ? Math.max(numberOrDefault(element.size.width, 300) - numberOrDefault(element.qrSize, 100) - 10, 80)
        : numberOrDefault(element.size.width, 300);
    const lineHeight = element.style.fontSize * 1.35;

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(element.style.fontSize);
    doc.setTextColor(element.style.color);
    doc.text(renderizarTextoComVariaveis(element.institutionText), textX, textY);

    doc.setFont(fontFamily, "normal");
    doc.text("Chave de validação: ", textX, textY + lineHeight);
    const keyLabelWidth = doc.getTextWidth("Chave de validação: ");
    doc.setFont(fontFamily, "bold");
    doc.text(
      `${renderizarTextoComVariaveis(element.validationKey)}. Data de emissão: ${renderizarTextoComVariaveis(element.issueDate)}`,
      textX + keyLabelWidth,
      textY + lineHeight
    );

    doc.setFont(fontFamily, "normal");
    const instructionLines = doc.splitTextToSize(
      renderizarTextoComVariaveis(element.instructionText),
      textWidth
    );
    doc.text(instructionLines, textX, textY + lineHeight * 2);
  } catch (error) {
    console.error("Erro ao adicionar QR Code no PDF:", error);
  }
};

const renderQrCodeInPdfPreview = (doc: jsPDF, element: CertificadoQrCode) => {
  try {
    doc.addImage(
      element.qrCodeDataUrl,
      getImageFormat(element.qrCodeDataUrl),
      numberOrDefault(element.position.x, 0),
      numberOrDefault(element.position.y, 0),
      numberOrDefault(element.qrSize, 100),
      numberOrDefault(element.qrSize, 100)
    );

    const fontFamily = getPdfFontFamily(element.style.fontFamily);
    const textX =
      element.textPosition === "right"
        ? numberOrDefault(element.position.x, 0) + numberOrDefault(element.qrSize, 100) + 10
        : numberOrDefault(element.position.x, 0);
    const textY =
      element.textPosition === "right"
        ? numberOrDefault(element.position.y, 0) + element.style.fontSize
        : numberOrDefault(element.position.y, 0) + numberOrDefault(element.qrSize, 100) + element.style.fontSize + 8;
    const textWidth =
      element.textPosition === "right"
        ? Math.max(numberOrDefault(element.size.width, 300) - numberOrDefault(element.qrSize, 100) - 10, 80)
        : numberOrDefault(element.size.width, 300);

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(element.style.fontSize);
    doc.setTextColor(element.style.color);
    const instructionLines = renderizarTextoComVariaveis(element.instructionText)
      .split("\n")
      .flatMap((paragraph) => doc.splitTextToSize(paragraph || " ", textWidth));
    doc.text(instructionLines, textX, textY);
  } catch (error) {
    console.error("Erro ao adicionar QR Code no PDF:", error);
  }
};

const criarPagina = (enabled: boolean, defaultElementsRemovable = false): CertificatePage => ({
  enabled,
  background: null,
  elements: criarTextosPadrao(defaultElementsRemovable),
});

const rolesPadraoRemoviveis = new Set(["title", "subtitle", "recipient_name", "main_text"]);

const normalizarElementosPagina = (
  elements: CertificadoElemento[] | undefined,
  defaultElementsRemovable = false,
) => {
  if (!Array.isArray(elements) || !elements.length) {
    return criarTextosPadrao(defaultElementsRemovable);
  }

  return elements.map((element) => {
    if (isQrCodeElemento(element)) {
      return {
        ...element,
        validationUrl: qrCodeValidationRoute,
        validationKey: element.validationKey || "{{codigo_certificado}}",
        issueDate: element.issueDate || "{{data_emissao_certificado}}",
        institutionText: element.institutionText ?? "",
        instructionText: element.instructionText || qrCodeValidacaoConfig.instructionText,
      };
    }

    if (!defaultElementsRemovable || !isTextoElemento(element)) {
      return element;
    }

    return rolesPadraoRemoviveis.has(element.role)
      ? {
          ...element,
          removable: true,
        }
      : element;
  });
};

const normalizarBackground = (background: BackgroundImage | null | undefined): BackgroundImage | null => {
  if (!background) return null;

  return {
    ...background,
    fit: background.fit || "contain",
  };
};

const normalizarPagina = (
  page: CertificatePage | null | undefined,
  fallbackEnabled: boolean,
  defaultElementsRemovable = false,
): CertificatePage => {
  if (!page) {
    return criarPagina(fallbackEnabled, defaultElementsRemovable);
  }

  return {
    enabled: page.enabled ?? fallbackEnabled,
    background: normalizarBackground(page.background),
    elements: normalizarElementosPagina(page.elements, defaultElementsRemovable),
  };
};

const mergeCertificateTemplateVariables = (
  variables: CertificateTemplateVariable[],
  groups: Record<string, CertificateTemplateVariable[]>,
) => {
  const extras: Array<{ group: string; variable: CertificateTemplateVariable }> = [
    { group: "turma", variable: { key: "{{turma.modalidade}}", label: "Modalidade da turma" } },
    { group: "turma", variable: { key: "{{turma.hora_inicio}}", label: "Hora de Início da turma" } },
    { group: "turma", variable: { key: "{{turma.hora_fim}}", label: "Hora de fim da turma" } },
    { group: "turma", variable: { key: "{{turma.endereco}}", label: "Endereco da turma" } },
    { group: "turma", variable: { key: "{{turma.cep}}", label: "CEP da turma" } },
    { group: "turma", variable: { key: "{{turma.logradouro}}", label: "Logradouro da turma" } },
    { group: "turma", variable: { key: "{{turma.numero}}", label: "Numero da turma" } },
    { group: "turma", variable: { key: "{{turma.complemento}}", label: "Complemento da turma" } },
    { group: "turma", variable: { key: "{{turma.bairro}}", label: "Bairro da turma" } },
    { group: "turma", variable: { key: "{{turma.cidade}}", label: "Cidade da turma" } },
    { group: "turma", variable: { key: "{{turma.uf}}", label: "UF da turma" } },
    { group: "turma", variable: { key: "{{instituicao_resp}}", label: "Instituicao responsavel da turma" } },
    { group: "acao", variable: { key: "{{acao.modalidade}}", label: "Modalidade da acao" } },
    { group: "acao", variable: { key: "{{acao.tipologia}}", label: "Tipologia da acao" } },
    { group: "acao", variable: { key: "{{acao.temas}}", label: "Temas da acao" } },
  ];

  const variableMap = new Map<string, CertificateTemplateVariable>();
  variables.forEach((variable) => variableMap.set(variable.key, variable));
  extras.forEach(({ variable }) => {
    if (!variableMap.has(variable.key)) {
      variableMap.set(variable.key, variable);
    }
  });

  const nextGroups: Record<string, CertificateTemplateVariable[]> = Object.fromEntries(
    Object.entries(groups || {}).map(([groupName, groupVariables]) => [groupName, [...groupVariables]])
  );

  extras.forEach(({ group, variable }) => {
    const currentGroup = nextGroups[group] || [];
    if (!currentGroup.some((item) => item.key === variable.key)) {
      nextGroups[group] = [...currentGroup, variable];
    }
  });

  return {
    variables: Array.from(variableMap.values()),
    groups: nextGroups,
  };
};

const normalizarTemplateParaEstado = (
  detail: CertificateTemplateDetail,
): {
  nomeModelo: string;
  documentType: DocumentType;
  orientacao: "landscape" | "portrait";
  active: boolean;
  pages: Record<SideKey, CertificatePage>;
} => ({
  nomeModelo: detail.name,
  documentType: detail.documentType,
  orientacao: detail.page.orientation,
  active: detail.active,
  pages: {
    front: normalizarPagina(detail.pages.front, true, true),
    back: normalizarPagina(detail.pages.back, Boolean(detail.pages.back), true),
  },
});

const CadastrarModeloCertificado: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const templateId = searchParams.get("id");
  const isEditing = Boolean(templateId);
  const [nomeModelo, setNomeModelo] = useState("Modelo padrao de certificado");
  const [documentType, setDocumentType] = useState<DocumentType>("certificate");
  const [orientacao, setOrientacao] = useState<"landscape" | "portrait">("landscape");
  const [active, setActive] = useState(true);

  useEffect(() => {
    void loadCertificatePreviewFonts().catch((error) => {
      console.error("Erro ao carregar fontes do preview do certificado:", error);
    });
  }, []);
  const [activeSide, setActiveSide] = useState<SideKey>("front");
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("elements");
  const [pages, setPages] = useState<Record<SideKey, CertificatePage>>({
    front: criarPagina(true, true),
    back: criarPagina(false, true),
  });
  const [elementoSelecionadoId, setElementoSelecionadoId] = useState("titulo");
  const [variaveisDisponiveis, setVariaveisDisponiveis] = useState<CertificateTemplateVariable[]>([]);
  const [gruposVariaveis, setGruposVariaveis] = useState<Record<string, CertificateTemplateVariable[]>>({});
  const [payloadGerado, setPayloadGerado] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);

  const activePage = pages[activeSide];
  const elementoSelecionado: CertificadoElemento | null =
    activePage.elements.find((item) => item.id === elementoSelecionadoId) || activePage.elements[0] || null;
  const textoSelecionado = elementoSelecionado && isTextoElemento(elementoSelecionado) ? elementoSelecionado : null;
  const imagemSelecionada = elementoSelecionado && isImagemElemento(elementoSelecionado) ? elementoSelecionado : null;
  const qrCodeSelecionado = elementoSelecionado && isQrCodeElemento(elementoSelecionado) ? elementoSelecionado : null;

  useEffect(() => {
    if (!textoSelecionado || !editorRef.current) return;

    const editor = editorRef.current;
    const isFocused = document.activeElement === editor;

    if (!isFocused && editor.innerHTML !== textoSelecionado.contentHtml) {
      editor.innerHTML = textoSelecionado.contentHtml;
    }
  }, [textoSelecionado?.id, textoSelecionado?.contentHtml]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  useEffect(() => {
    const loadPageData = async () => {
      setLoadingInitialData(true);

      try {
        const [variablesResponse, templateResponse] = await Promise.all([
          findCertificateTemplateVariables(),
          templateId ? findCertificateTemplate(templateId) : Promise.resolve(null),
        ]);

        const mergedVariables = mergeCertificateTemplateVariables(
          variablesResponse.variables || [],
          variablesResponse.groups || {},
        );

        setVariaveisDisponiveis(mergedVariables.variables);
        setGruposVariaveis(mergedVariables.groups);

        if (templateResponse) {
          const normalizedTemplate = normalizarTemplateParaEstado(templateResponse);
          setNomeModelo(normalizedTemplate.nomeModelo);
          setDocumentType(normalizedTemplate.documentType);
          setOrientacao(normalizedTemplate.orientacao);
          setActive(normalizedTemplate.active);
          setPages(normalizedTemplate.pages);
          setActiveSide("front");
          setElementoSelecionadoId(normalizedTemplate.pages.front.elements[0]?.id || "");
        } else {
          setElementoSelecionadoId(criarPagina(true, true).elements[0].id);
        }
      } catch (error) {
        setMsgError("Nao foi possivel carregar os dados do modelo de certificado.");
      } finally {
        setLoadingInitialData(false);
      }
    };

    loadPageData();
  }, [templateId]);

  const payload = useMemo<CertificateTemplatePayload>(
    () => ({
      name: nomeModelo,
      documentType,
      documentTypeLabel: documentType === "certificate" ? "Certificado" : "Declaração",
      page: {
        size: "A4",
        orientation: orientacao,
        unit: "px",
      },
      variables: variaveisDisponiveis.map((variavel) => variavel.key),
      pages: {
        front: {
          enabled: true,
          background: pages.front.background,
          elements: pages.front.elements,
        },
        back: pages.back.enabled
          ? {
              enabled: true,
              background: pages.back.background,
              elements: pages.back.elements,
            }
          : null,
      },
      active,
    }),
    [active, documentType, nomeModelo, orientacao, pages, variaveisDisponiveis]
  );

  const atualizarPaginaAtiva = (updater: (page: CertificatePage) => CertificatePage) => {
    setPages((current) => ({
      ...current,
      [activeSide]: updater(current[activeSide]),
    }));
    setPayloadGerado(false);
  };

  const atualizarElemento = (
    id: string,
    updater: (elemento: CertificadoElemento) => CertificadoElemento
  ) => {
    atualizarPaginaAtiva((page) => ({
      ...page,
      elements: page.elements.map((item) => (item.id === id ? updater(item) : item)),
    }));
  };

  const atualizarHtmlSelecionado = (html: string) => {
    if (!elementoSelecionado) return;

    atualizarElemento(elementoSelecionado.id, (elemento) => ({
      ...elemento,
      ...(isTextoElemento(elemento) ? { contentHtml: html } : {}),
    }));
  };

  const executarComandoEditor = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    atualizarHtmlSelecionado(editorRef.current?.innerHTML || "");
  };

  const adicionarTexto = () => {
    const customTextCount = activePage.elements.filter(
      (item) => isTextoElemento(item) && item.role === "custom"
    ).length;
    const novoElemento: CertificadoTexto = {
      id: `texto-${Date.now()}`,
      label: `Texto personalizado ${customTextCount + 1}`,
      role: "custom",
      type: "html_text",
      removable: true,
      contentHtml: "<p>Novo texto do documento</p>",
      style: {
        fontFamily: "Arial",
        fontSize: 18,
        color: "#111827",
        align: "center",
      },
      position: { x: 160, y: 410 },
      size: { width: 580 },
    };

    atualizarPaginaAtiva((page) => ({
      ...page,
      elements: [...page.elements, novoElemento],
    }));
    setElementoSelecionadoId(novoElemento.id);
  };

  const adicionarImagem = (file?: File) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const image = new Image();

      const criarElementoImagem = (width: number, height: number) => {
        const imageCount = activePage.elements.filter(isImagemElemento).length;
        const novaImagem: CertificadoImagem = {
          id: `imagem-${Date.now()}`,
          label: `Imagem ${imageCount + 1}`,
          type: "image",
          removable: true,
          fileName: file.name,
          dataUrl,
          position: { x: 80, y: 80 },
          size: { width, height },
        };

        atualizarPaginaAtiva((page) => ({
          ...page,
          elements: [...page.elements, novaImagem],
        }));
        setElementoSelecionadoId(novaImagem.id);
      };

      image.onload = () => {
        criarElementoImagem(image.naturalWidth || image.width, image.naturalHeight || image.height);
      };

      image.onerror = () => {
        criarElementoImagem(180, 100);
      };

      image.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const adicionarQrCodeValidacao = async () => {
    const qrCodeCount = activePage.elements.filter(isQrCodeElemento).length;
    const qrCodeDataUrl = await QRCode.toDataURL(buildQrValidationUrl(qrCodeValidacaoConfig.validationUrl), {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 220,
    });
    const novoQrCode: CertificadoQrCode = {
      id: `qrcode-validacao-${Date.now()}`,
      label: `QR Code de validação ${qrCodeCount + 1}`,
      type: "validation_qrcode",
      removable: true,
      ...qrCodeValidacaoConfig,
      qrCodeDataUrl,
      textPosition: "right",
      style: {
        fontFamily: "Arial",
        fontSize: 14,
        color: "#111827",
      },
      position: { x: 40, y: 480 },
      size: { width: 760, height: 120 },
      qrSize: 110,
    };

    atualizarPaginaAtiva((page) => ({
      ...page,
      elements: [...page.elements, novoQrCode],
    }));
    setElementoSelecionadoId(novoQrCode.id);
  };

  const removerTexto = (id: string) => {
    const elemento = activePage.elements.find((item) => item.id === id);
    if (!elemento?.removable) return;

    atualizarPaginaAtiva((page) => ({
      ...page,
      elements: page.elements.filter((item) => item.id !== id),
    }));
    const remainingElements = activePage.elements.filter((item) => item.id !== id);
    setElementoSelecionadoId(remainingElements[0]?.id || "");
  };

  const inserirVariavel = (variavel: string) => {
    if (!textoSelecionado) return;
    executarComandoEditor("insertText", variavel);
  };

  const atualizarQrCodeSelecionado = async (
    updater: (elemento: CertificadoQrCode) => CertificadoQrCode | Promise<CertificadoQrCode>
  ) => {
    if (!qrCodeSelecionado) return;

    const updatedQrCode = await updater(qrCodeSelecionado);
    atualizarElemento(qrCodeSelecionado.id, (elemento) =>
      isQrCodeElemento(elemento) ? updatedQrCode : elemento
    );
  };

  const _atualizarQrCodeUrl = async (validationUrl: string) => {
    if (!qrCodeSelecionado) return;

    const qrCodeDataUrl = await QRCode.toDataURL(validationUrl || " ", {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 220,
    });

    atualizarElemento(qrCodeSelecionado.id, (elemento) =>
      isQrCodeElemento(elemento)
        ? {
            ...elemento,
            validationUrl,
            qrCodeDataUrl,
          }
        : elemento
    );
  };

  const carregarImagemBackground = (file?: File) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      atualizarPaginaAtiva((page) => ({
        ...page,
        background: {
          fileName: file.name,
          dataUrl: String(reader.result),
          fit: "cover",
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  const alternarVerso = (enabled: boolean) => {
    setPages((current) => ({
      ...current,
      back: {
        ...current.back,
        enabled,
      },
    }));
    setActiveSide(enabled ? "back" : "front");
    setElementoSelecionadoId(enabled ? pages.back.elements[0].id : pages.front.elements[0].id);
    setPayloadGerado(false);
  };

  const trocarLado = (side: SideKey) => {
    if (side === "back" && !pages.back.enabled) return;
    setActiveSide(side);
    setElementoSelecionadoId(pages[side].elements[0].id);
  };

  const adicionarBackgroundNoPdf = (doc: jsPDF, background: BackgroundImage | null) => {
    if (!background) return;

    const { width: pageWidth, height: pageHeight } = getPdfPageSize(orientacao);
    const imageFormat = getImageFormat(background.dataUrl);

    try {
      if (background.fit === "contain" || background.fit === "cover") {
        const imageProps = doc.getImageProperties(background.dataUrl);
        const imageRatio = imageProps.width / imageProps.height;
        const pageRatio = pageWidth / pageHeight;
        const shouldFitByWidth = imageRatio > pageRatio;
        const imageWidth = shouldFitByWidth ? pageWidth : pageHeight * imageRatio;
        const imageHeight = shouldFitByWidth ? pageWidth / imageRatio : pageHeight;
        const imageX = (pageWidth - imageWidth) / 2;
        const imageY = (pageHeight - imageHeight) / 2;

        doc.addImage(background.dataUrl, imageFormat, imageX, imageY, imageWidth, imageHeight);
        return;
      }

      doc.addImage(background.dataUrl, imageFormat, 0, 0, pageWidth, pageHeight);
    } catch (error) {
      console.error("Erro ao adicionar imagem de fundo no PDF:", error);
    }
  };

  const adicionarPaginaModeloNoPdf = (doc: jsPDF, page: CertificatePage) => {
    adicionarBackgroundNoPdf(doc, page.background);
    page.elements.forEach((elemento) => {
      if (isTextoElemento(elemento)) {
        renderHtmlTextInPdf(doc, elemento);
        return;
      }

      if (isImagemElemento(elemento)) {
        renderImageInPdf(doc, elemento);
        return;
      }

      renderQrCodeInPdfPreview(doc, elemento);
    });
  };

  const gerarPdfModelo = async () => {
    const { width, height } = getPdfPageSize(orientacao);
    const doc = new jsPDF({
      orientation: orientacao,
      unit: "px",
      format: [width, height],
      compress: true,
    });
    try {
      await registerCertificatePdfFonts(doc);
    } catch (error) {
      console.error("Erro ao carregar fontes do modelo de certificado:", error);
      setMsgError("Não foi possível carregar as fontes necessárias para gerar o PDF.");
      return;
    }

    adicionarPaginaModeloNoPdf(doc, pages.front);

    if (pages.back.enabled) {
      doc.addPage([width, height], orientacao);
      adicionarPaginaModeloNoPdf(doc, pages.back);
    }

    const filename = `${nomeModelo.trim() || "modelo-certificado"}.pdf`
      .replace(/[\\/:*?"<>|]/g, "-")
      .toLowerCase();

    doc.save(filename);
  };

  const salvarModelo = async () => {
    try {
      setLoadingSave(true);
      setMsgError(null);

      if (isEditing && templateId) {
        await updateCertificateTemplate(templateId, payload);
        setMsgSuccess("Modelo atualizado com sucesso.");
      } else {
        await createCertificateTemplate(payload);
        setMsgSuccess("Modelo cadastrado com sucesso.");
      }

      navigate("/modelos-certificados");
    } catch (error) {
      setMsgError("Nao foi possivel salvar o modelo de certificado.");
    } finally {
      setLoadingSave(false);
    }
  };

  const variaveisAgrupadas = useMemo(() => {
    const groupEntries = Object.entries(gruposVariaveis || {}).filter(([, items]) => items?.length);

    if (groupEntries.length > 0) {
      return groupEntries;
    }

    if (!variaveisDisponiveis.length) {
      return [];
    }

    return [["disponiveis", variaveisDisponiveis] as [string, CertificateTemplateVariable[]]];
  }, [gruposVariaveis, variaveisDisponiveis]);

  if (loadingInitialData) {
    return (
      <Card className={styles.card}>
        <CardContent>
          <div className={styles.title}>
            {isEditing ? "Editar Modelo de Certificado" : "Cadastrar Modelo de Certificado"}
          </div>
          <div className={styles.loadingState}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          {isEditing ? "Editar Modelo de Certificado" : "Cadastrar Modelo de Certificado"}
        </div>

        {msgSuccess && (
          <div className={stylesGeneral.mt4}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={stylesGeneral.mt4}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        {payloadGerado && (
          <div className={stylesGeneral.mt4}>
            <Alert severity="info">
              Payload gerado para conferencia. Nenhuma informacao foi enviada para a API.
            </Alert>
          </div>
        )}

        <div className={`${stylesGrid.grid12} ${stylesGeneral.mt4}`}>
          <div className={stylesGrid.inputGroup6}>
            <label className={stylesInput.titleInput}>Nome do modelo</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={nomeModelo}
              onChange={(event) => setNomeModelo(event.target.value)}
              InputProps={{
                className: stylesInput.input,
                disableUnderline: true,
              }}
            />
          </div>

          <div className={stylesGrid.inputGroup2}>
            <label className={stylesInput.titleInput}>Tipo</label>
            <Select
              size="small"
              fullWidth
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value as DocumentType)}
              className={styles.select}
            >
              <MenuItem value="certificate">Certificado</MenuItem>
              <MenuItem value="declaration">Declaração</MenuItem>
            </Select>
          </div>

          <div className={stylesGrid.inputGroup2}>
            <label className={stylesInput.titleInput}>Orientação</label>
            <Select
              size="small"
              fullWidth
              value={orientacao}
              onChange={(event) => setOrientacao(event.target.value as "landscape" | "portrait")}
              className={styles.select}
            >
              <MenuItem value="landscape">Paisagem</MenuItem>
              <MenuItem value="portrait">Retrato</MenuItem>
            </Select>
          </div>

          <div className={stylesGrid.inputGroup2}>
            <label className={stylesInput.titleInput}>Status</label>
            <Select
              size="small"
              fullWidth
              value={active ? "active" : "inactive"}
              onChange={(event) => setActive(event.target.value === "active")}
              className={styles.select}
            >
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="inactive">Inativo</MenuItem>
            </Select>
          </div>

          <div className={stylesGrid.inputGroupFull}>
            <label className={stylesInput.titleInput}>Paginas do documento</label>
            <div className={styles.sideToolbar}>
              <Button
                variant="contained"
                onClick={() => trocarLado("front")}
                className={activeSide === "front" ? styles.sideButtonActive : styles.sideButton}
              >
                Frente
              </Button>
              <Button
                variant="contained"
                disabled={!pages.back.enabled}
                onClick={() => trocarLado("back")}
                className={activeSide === "back" ? styles.sideButtonActive : styles.sideButton}
              >
                Verso
              </Button>
              <Button
                variant="outlined"
                onClick={() => alternarVerso(!pages.back.enabled)}
                className={styles.optionalButton}
              >
                {pages.back.enabled ? "Remover verso" : "Adicionar verso"}
              </Button>
            </div>
          </div>

          <div className={stylesGrid.inputGroup4}>
            <label className={stylesInput.titleInput}>Imagem de fundo da {activeSide === "front" ? "frente" : "verso"}</label>
            <Button variant="outlined" component="label" className={styles.uploadButton}>
              Carregar imagem
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => carregarImagemBackground(event.target.files?.[0])}
              />
            </Button>
            {activePage.background && (
              <div className={styles.fileName}>{activePage.background.fileName}</div>
            )}
          </div>

          <div className={stylesGrid.inputGroup4}>
            <label className={stylesInput.titleInput}>Ajuste da imagem</label>
            <Select
              size="small"
              fullWidth
              value={activePage.background?.fit || "cover"}
              disabled={!activePage.background}
              onChange={(event) =>
                atualizarPaginaAtiva((page) => ({
                  ...page,
                  background: page.background
                    ? { ...page.background, fit: event.target.value as BackgroundImage["fit"] }
                    : null,
                }))
              }
              className={styles.select}
            >
              <MenuItem value="cover">Preencher sem cortar</MenuItem>
              <MenuItem value="contain">Conter</MenuItem>
              <MenuItem value="stretch">Esticar</MenuItem>
            </Select>
          </div>

          <div className={`${stylesGrid.inputGroup4} ${stylesGrid.itemsEnd}`}>
            <Button
              variant="outlined"
              disabled={!activePage.background}
              onClick={() => atualizarPaginaAtiva((page) => ({ ...page, background: null }))}
              className={styles.removeButton}
            >
              Remover imagem
            </Button>
          </div>
        </div>

        <div className={styles.workspace}>
          <aside className={styles.sidebarPanel}>
            <div className={styles.panelHeader}>
              <span>Configuracao lateral</span>
            </div>

            <Tabs
              value={sidebarTab}
              onChange={(_, value) => setSidebarTab(value as SidebarTab)}
              className={styles.sidebarTabs}
            >
              <Tab label="Elementos" value="elements" className={styles.sidebarTab} />
              <Tab label="Variaveis" value="variables" className={styles.sidebarTab} />
            </Tabs>

            {sidebarTab === "elements" ? (
              <>
                <div className={styles.panelHeader}>
                  <span>Elementos da {activeSide === "front" ? "frente" : "verso"}</span>
                  <Tooltip title="Adicionar texto personalizado">
                    <IconButton size="small" onClick={adicionarTexto} className={styles.iconButton}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>

                <div className={styles.elementList}>
                  {activePage.elements.map((elemento) => (
                    <div key={elemento.id} className={styles.elementRow}>
                      <button
                        type="button"
                        className={
                          elemento.id === elementoSelecionadoId
                            ? styles.elementButtonActive
                            : styles.elementButton
                        }
                        onClick={() => setElementoSelecionadoId(elemento.id)}
                      >
                        {elemento.label}
                      </button>
                      {elemento.removable && (
                        <Tooltip title="Remover texto">
                          <IconButton
                            size="small"
                            onClick={() => removerTexto(elemento.id)}
                            className={styles.deleteButton}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  variant="outlined"
                  component="label"
                  className={styles.addImageButton}
                  startIcon={<AddPhotoAlternateOutlined fontSize="small" />}
                >
                  Adicionar imagem
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => adicionarImagem(event.target.files?.[0])}
                  />
                </Button>

                <Button
                  variant="outlined"
                  className={styles.addImageButton}
                  startIcon={<QrCode2Outlined fontSize="small" />}
                  onClick={adicionarQrCodeValidacao}
                >
                  Adicionar QR Code
                </Button>
              </>
            ) : (
              <div className={styles.variableBox}>
                <span className={styles.sectionLabel}>Variaveis disponiveis</span>

                {variaveisAgrupadas.length === 0 ? (
                  <Alert severity="info" className={styles.richTextAlert}>
                    Nenhuma variavel foi disponibilizada pela API.
                  </Alert>
                ) : (
                  variaveisAgrupadas.map(([groupName, variables]) => (
                    <div key={groupName} className={styles.variableGroup}>
                      <span className={styles.variableGroupTitle}>{groupName}</span>
                      <div className={styles.variableList}>
                        {variables.map((variavel) => (
                          <Button
                            key={variavel.key}
                            size="small"
                            variant="outlined"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => inserirVariavel(variavel.key)}
                            className={styles.variableButton}
                          >
                            {variavel.key}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </aside>

          <section className={styles.editorPanel}>
            <div className={styles.panelHeader}>
              <span>
                {textoSelecionado
                  ? "Editor do texto"
                  : qrCodeSelecionado
                    ? "QR Code de validação"
                    : "Imagem selecionada"}
              </span>
            </div>

            {textoSelecionado ? (
              <>
                <Alert severity="info" className={styles.richTextAlert}>
                  Selecione um trecho dentro do editor e use a barra de ferramentas para aplicar formatacao. O payload salva o HTML final em contentHtml.
                </Alert>

                <div className={stylesGrid.grid12}>
                  <div className={stylesGrid.inputGroup4}>
                    <label className={stylesInput.titleInput}>Fonte</label>
                    <Select
                      size="small"
                      fullWidth
                      value={textoSelecionado.style.fontFamily}
                      onChange={(event) =>
                        atualizarElemento(textoSelecionado.id, (elemento) =>
                          isTextoElemento(elemento)
                            ? {
                                ...elemento,
                                style: { ...elemento.style, fontFamily: event.target.value },
                              }
                            : elemento
                        )
                      }
                      className={styles.select}
                    >
                      {fontesDisponiveis.map((fonte) => (
                        <MenuItem key={fonte} value={fonte}>
                          {fonte}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Tamanho</label>
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      fullWidth
                      value={inputNumberValue(textoSelecionado.style.fontSize)}
                      onChange={(event) =>
                        atualizarElemento(textoSelecionado.id, (elemento) =>
                          isTextoElemento(elemento)
                            ? {
                                ...elemento,
                                style: { ...elemento.style, fontSize: parseOptionalNumber(event.target.value) ?? 0 },
                              }
                            : elemento
                        )
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Cor</label>
                    <input
                      type="color"
                      value={textoSelecionado.style.color}
                      className={styles.colorInput}
                      onChange={(event) =>
                        atualizarElemento(textoSelecionado.id, (elemento) =>
                          isTextoElemento(elemento)
                            ? {
                                ...elemento,
                                style: { ...elemento.style, color: event.target.value },
                              }
                            : elemento
                        )
                      }
                    />
                  </div>

                  <div className={stylesGrid.inputGroupFull}>
                    <div className={styles.ckeditorBox}>
                      <div className={styles.ckeditorToolbar}>
                        <Tooltip title="Negrito">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => executarComandoEditor("bold")}
                            className={styles.toolButton}
                          >
                            <FormatBold fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Italico">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => executarComandoEditor("italic")}
                            className={styles.toolButton}
                          >
                            <FormatItalic fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sublinhado">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => executarComandoEditor("underline")}
                            className={styles.toolButton}
                          >
                            <FormatUnderlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Alinhar a esquerda">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              executarComandoEditor("justifyLeft");
                              atualizarElemento(textoSelecionado.id, (elemento) =>
                                isTextoElemento(elemento)
                                  ? { ...elemento, style: { ...elemento.style, align: "left" } }
                                  : elemento
                              );
                            }}
                            className={styles.toolButton}
                          >
                            <FormatAlignLeft fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Centralizar">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              executarComandoEditor("justifyCenter");
                              atualizarElemento(textoSelecionado.id, (elemento) =>
                                isTextoElemento(elemento)
                                  ? { ...elemento, style: { ...elemento.style, align: "center" } }
                                  : elemento
                              );
                            }}
                            className={styles.toolButton}
                          >
                            <FormatAlignCenter fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Alinhar a direita">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              executarComandoEditor("justifyRight");
                              atualizarElemento(textoSelecionado.id, (elemento) =>
                                isTextoElemento(elemento)
                                  ? { ...elemento, style: { ...elemento.style, align: "right" } }
                                  : elemento
                              );
                            }}
                            className={styles.toolButton}
                          >
                            <FormatAlignRight fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <div
                        key={textoSelecionado.id}
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        className={styles.ckeditorEditable}
                        style={{
                          fontFamily: getPreviewFontFamily(textoSelecionado.style.fontFamily),
                          fontSize: `${textoSelecionado.style.fontSize}px`,
                          color: textoSelecionado.style.color,
                          textAlign: textoSelecionado.style.align,
                        }}
                        onInput={(event) => atualizarHtmlSelecionado(event.currentTarget.innerHTML)}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : qrCodeSelecionado ? (
              <div className={styles.imageInfoPanel}>
                <Alert severity="info" className={styles.richTextAlert}>
                  Este bloco representa a validacao do certificado. O QR Code sera gerado automaticamente com a rota /validar-certificado?chave=
                  {"{{codigo_certificado}}"}.
                </Alert>

                <div className={stylesGrid.grid12}>
                  <div className={stylesGrid.inputGroup8} style={{ display: "none" }}>
                    <label className={stylesInput.titleInput}>URL de validação</label>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      value={buildQrValidationUrl(qrCodeSelecionado.validationUrl)}
                      disabled
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup4}>
                    <label className={stylesInput.titleInput}>Posição do texto</label>
                    <Select
                      size="small"
                      fullWidth
                      value={qrCodeSelecionado.textPosition}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          textPosition: event.target.value as CertificadoQrCode["textPosition"],
                        }))
                      }
                      className={styles.select}
                    >
                      <MenuItem value="right">Ao lado</MenuItem>
                      <MenuItem value="bottom">Abaixo</MenuItem>
                    </Select>
                  </div>

                  <div className={stylesGrid.inputGroup4} style={{ display: "none" }}>
                    <label className={stylesInput.titleInput}>Chave de validação</label>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      value={qrCodeSelecionado.validationKey}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          validationKey: event.target.value,
                        }))
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup4} style={{ display: "none" }}>
                    <label className={stylesInput.titleInput}>Data de emissão</label>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      value={qrCodeSelecionado.issueDate}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          issueDate: event.target.value,
                        }))
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Fonte</label>
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      fullWidth
                      value={inputNumberValue(qrCodeSelecionado.style.fontSize)}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          style: { ...elemento.style, fontSize: parseOptionalNumber(event.target.value) ?? 0 },
                        }))
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Cor</label>
                    <input
                      type="color"
                      value={qrCodeSelecionado.style.color}
                      className={styles.colorInput}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          style: { ...elemento.style, color: event.target.value },
                        }))
                      }
                    />
                  </div>

                  <div className={stylesGrid.inputGroupFull} style={{ display: "none" }}>
                    <label className={stylesInput.titleInput}>Texto da instituição</label>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      value={qrCodeSelecionado.institutionText}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          institutionText: event.target.value,
                        }))
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroupFull}>
                    <label className={stylesInput.titleInput}>Texto de instrução</label>
                    <TextField
                      multiline
                      minRows={3}
                      size="small"
                      variant="standard"
                      fullWidth
                      value={qrCodeSelecionado.instructionText}
                      onChange={(event) =>
                        atualizarQrCodeSelecionado((elemento) => ({
                          ...elemento,
                          instructionText: event.target.value,
                        }))
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                      helperText="Use as variaveis {{codigo_certificado}} e {{data_emissao_certificado}} no texto."
                    />
                  </div>

                  <div className={stylesGrid.inputGroupFull}>
                    <div className={styles.qrEditorPreview}>
                      <img src={qrCodeSelecionado.qrCodeDataUrl} alt={qrCodeSelecionado.label} />
                      <div>
                        <strong>{qrCodeSelecionado.label}</strong>
                        <span>{buildQrValidationUrl(qrCodeSelecionado.validationUrl)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : imagemSelecionada ? (
              <div className={styles.imageInfoPanel}>
                <Alert severity="info" className={styles.richTextAlert}>
                  Esta imagem e independente do background. Use os campos de posicao e tamanho acima do preview para ajustar X, Y, largura e altura.
                </Alert>
                <div className={styles.imageInfoContent}>
                  <img src={imagemSelecionada.dataUrl} alt={imagemSelecionada.label} />
                  <div>
                    <strong>{imagemSelecionada.label}</strong>
                    <span>{imagemSelecionada.fileName}</span>
                  </div>
                </div>
              </div>
            ) : (
              <Alert severity="info" className={styles.richTextAlert}>
                Nenhum elemento selecionado. Adicione um texto, imagem ou QR Code para configurar o verso.
              </Alert>
            )}
          </section>
        </div>

        <div className={styles.previewGrid}>
          <section className={styles.positionPanelTop}>
            <div className={styles.panelHeader}>
              <span>Posicao e tamanho do elemento</span>
            </div>

            {elementoSelecionado ? (
              <div className={stylesGrid.grid12}>
                <div className={stylesGrid.inputGroup3}>
                  <label className={stylesInput.titleInput}>Elemento selecionado</label>
                  <TextField
                    size="small"
                    variant="standard"
                    fullWidth
                    value={elementoSelecionado.label}
                    disabled
                    InputProps={{
                      className: stylesInput.input,
                      disableUnderline: true,
                    }}
                  />
                </div>

                <div className={stylesGrid.inputGroup3}>
                  <label className={stylesInput.titleInput}>Posicao X</label>
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    fullWidth
                    value={inputNumberValue(elementoSelecionado.position.x)}
                    onChange={(event) =>
                      atualizarElemento(elementoSelecionado.id, (elemento) => ({
                        ...elemento,
                        position: { ...elemento.position, x: parseOptionalNumber(event.target.value) },
                      }))
                    }
                    InputProps={{
                      className: stylesInput.input,
                      disableUnderline: true,
                    }}
                  />
                </div>

                <div className={stylesGrid.inputGroup3}>
                  <label className={stylesInput.titleInput}>Posicao Y</label>
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    fullWidth
                    value={inputNumberValue(elementoSelecionado.position.y)}
                    onChange={(event) =>
                      atualizarElemento(elementoSelecionado.id, (elemento) => ({
                        ...elemento,
                        position: { ...elemento.position, y: parseOptionalNumber(event.target.value) },
                      }))
                    }
                    InputProps={{
                      className: stylesInput.input,
                      disableUnderline: true,
                    }}
                  />
                </div>

                <div className={stylesGrid.inputGroup3}>
                  <label className={stylesInput.titleInput}>Largura</label>
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    fullWidth
                    value={inputNumberValue(elementoSelecionado.size.width)}
                    onChange={(event) =>
                      atualizarElemento(elementoSelecionado.id, (elemento) => {
                        if (isImagemElemento(elemento) || isQrCodeElemento(elemento)) {
                          return {
                            ...elemento,
                            size: { ...elemento.size, width: parseOptionalNumber(event.target.value) },
                          };
                        }

                        return {
                          ...elemento,
                          size: { width: parseOptionalNumber(event.target.value) },
                        };
                      })
                    }
                    InputProps={{
                      className: stylesInput.input,
                      disableUnderline: true,
                    }}
                  />
                </div>

                {(imagemSelecionada || qrCodeSelecionado) && (
                  <div className={stylesGrid.inputGroup3}>
                    <label className={stylesInput.titleInput}>Altura</label>
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      fullWidth
                      value={inputNumberValue((imagemSelecionada || qrCodeSelecionado)?.size.height)}
                      onChange={(event) =>
                        atualizarElemento(elementoSelecionado.id, (elemento) =>
                          isImagemElemento(elemento) || isQrCodeElemento(elemento)
                            ? {
                                ...elemento,
                                size: { ...elemento.size, height: parseOptionalNumber(event.target.value) },
                              }
                            : elemento
                        )
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>
                )}

                {qrCodeSelecionado && (
                  <div className={stylesGrid.inputGroup3}>
                    <label className={stylesInput.titleInput}>Tamanho do QR</label>
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      fullWidth
                      value={inputNumberValue(qrCodeSelecionado.qrSize)}
                      onChange={(event) =>
                        atualizarElemento(qrCodeSelecionado.id, (elemento) =>
                          isQrCodeElemento(elemento)
                            ? {
                                ...elemento,
                                qrSize: parseOptionalNumber(event.target.value),
                              }
                            : elemento
                        )
                      }
                      InputProps={{
                        className: stylesInput.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <Alert severity="info" className={styles.richTextAlert}>
                Nenhum elemento selecionado. Adicione um texto, imagem ou QR Code para configurar posicao e tamanho.
              </Alert>
            )}
          </section>

          <section className={styles.previewPanel}>
            <div className={styles.panelHeader}>
              <span>Preview da {activeSide === "front" ? "frente" : "verso"} com dados de exemplo</span>
            </div>
            <div className={styles.certificateViewport}>
              <div
                className={orientacao === "landscape" ? styles.certificateLandscape : styles.certificatePortrait}
                style={{
                  backgroundImage: activePage.background
                    ? `url(${activePage.background.dataUrl})`
                    : "linear-gradient(135deg, rgba(90, 129, 250, 0.08), rgba(255, 255, 255, 0) 45%)",
                  backgroundSize:
                    activePage.background?.fit === "stretch"
                      ? "100% 100%"
                      : activePage.background
                        ? "contain"
                        : "auto",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                }}
              >
                {activePage.elements.map((elemento) => (
                  isTextoElemento(elemento) ? (
                    <div
                      key={elemento.id}
                      className={styles.previewText}
                      style={{
                        left: `${numberOrDefault(elemento.position.x, 0)}px`,
                        top: `${numberOrDefault(elemento.position.y, 0)}px`,
                        width: `${numberOrDefault(elemento.size.width, 200)}px`,
                        color: elemento.style.color,
                        fontFamily: getPreviewFontFamily(elemento.style.fontFamily),
                        fontSize: `${elemento.style.fontSize}px`,
                        textAlign: elemento.style.align,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: renderizarHtmlComVariaveis(elemento.contentHtml),
                      }}
                    />
                  ) : isImagemElemento(elemento) ? (
                    <img
                      key={elemento.id}
                      src={elemento.dataUrl}
                      alt={elemento.label}
                      className={styles.previewImage}
                      style={{
                        left: `${numberOrDefault(elemento.position.x, 0)}px`,
                        top: `${numberOrDefault(elemento.position.y, 0)}px`,
                        width: `${numberOrDefault(elemento.size.width, 100)}px`,
                        height: `${numberOrDefault(elemento.size.height, 100)}px`,
                      }}
                    />
                  ) : (
                    <div
                      key={elemento.id}
                      className={
                        elemento.textPosition === "right"
                          ? styles.previewQrBlockRight
                          : styles.previewQrBlockBottom
                      }
                      style={{
                        left: `${numberOrDefault(elemento.position.x, 0)}px`,
                        top: `${numberOrDefault(elemento.position.y, 0)}px`,
                        width: `${numberOrDefault(elemento.size.width, 300)}px`,
                        height: `${numberOrDefault(elemento.size.height, 120)}px`,
                        fontFamily: getPreviewFontFamily(elemento.style.fontFamily),
                        fontSize: `${elemento.style.fontSize}px`,
                        color: elemento.style.color,
                      }}
                    >
                      <img
                        src={elemento.qrCodeDataUrl}
                        alt={elemento.label}
                        style={{
                          width: `${numberOrDefault(elemento.qrSize, 100)}px`,
                          height: `${numberOrDefault(elemento.qrSize, 100)}px`,
                        }}
                      />
                      <div className={styles.previewQrText}>
                        <div style={{ display: "none" }}>{renderizarTextoComVariaveis(elemento.institutionText)}</div>
                        <div style={{ display: "none" }}>
                          Chave de validação: <strong>{renderizarTextoComVariaveis(elemento.validationKey)}</strong>.
                          {" "}Data de emissão: <strong>{renderizarTextoComVariaveis(elemento.issueDate)}</strong>
                        </div>
                        <div style={{ whiteSpace: "pre-line" }}>{renderizarTextoComVariaveis(elemento.instructionText)}</div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          </section>
          
          <section className={styles.sectionBlock}>
            {/*
            <div className={styles.panelHeader}>
              <span>Payload JSON</span>
            </div>
            */}
            {/*<pre className={styles.payload}>{JSON.stringify(payload, null, 2)}</pre>*/}

            <div className={styles.actions}>
              <Button
                variant="contained"
                className={stylesButton.buttonSquareSecondary}
                onClick={() => navigate("/modelos-certificados")}
              >
                Voltar
              </Button>
              
              {/*<Button
                variant="contained"
                className={stylesButton.buttonSquarePrimary}
                onClick={() => setPayloadGerado(true)}
              >
                Gerar payload
              </Button>*/}

              <Button
                variant="contained"
                className={stylesButton.buttonSquareSuccess}
                onClick={gerarPdfModelo}
              >
                Gerar Modelo em PDF
              </Button>

              <Button
                variant="contained"
                className={`${stylesButton.buttonSquarePrimary} ${loadingSave ? styles.disabledAction : ""}`}
                onClick={salvarModelo}
                disabled={loadingSave}
              >
                {loadingSave ? "Salvando..." : isEditing ? "Atualizar modelo" : "Salvar modelo"}
              </Button>
              
            </div>
          </section>

        </div>
      </CardContent>
    </Card>
  );
};

export default CadastrarModeloCertificado;
